
import { mapGetters, mapState } from 'vuex';
import FInfoTooltip from '@/components/molecules/tooltips/info-tooltip';
import RecommendationCard from '@/components/molecules/cards/recommendation-card';
import breakpointHelper from '@/mixins/breakpoint-helper';
import { YES_NO_OPTIONS } from '@/utils/common-constants';
import {
  PENSION_CALCULATOR_CTA_LINKS,
  PENSION_SOLUTION,
} from '@/utils/pension-calculator-constants';

export default {
  name: 'PlaygroundComparisonCards',

  components: {
    FInfoTooltip,
    RecommendationCard,
  },

  mixins: [breakpointHelper],

  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    retirementCalculatorAbExperiment: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      ctaText: this.$t('ctaText'),
      extraToggleTitle: this.$t('extraToggleTitle'),
    };
  },

  computed: {
    ...mapGetters({
      playgroundSolutionCardsData:
        'pension-calculator/playgroundSolutionCardsData',
      isLoggedIn: 'lead/isLoggedIn',
    }),

    ...mapState({
      canSuggestOwnHome: (state) =>
        state['pension-calculator'].inputs.boolean_home_owner ===
        YES_NO_OPTIONS.no,

      recommendation: (state) =>
        state['pension-calculator'].outputs.card_recommendation,

      ownHomeExpectedRetirementAge: (state) =>
        state['pension-calculator'].outputs.expected_retirement_own_home,
      privatePensionPlanExpectedRetirementAge: (state) =>
        state['pension-calculator'].outputs.expected_retirement_ppp,
      privatePensionPlanWorstRetirementAge: (state) =>
        state['pension-calculator'].outputs.worst_retirement_ppp,
      privatePensionPlanBestRetirementAge: (state) =>
        state['pension-calculator'].outputs.best_retirement_ppp,
      investmentPropertyExpectedRetirementAge: (state) =>
        state['pension-calculator'].outputs
          .expected_retirement_investment_property,
      investmentPropertyWorstRetirementAge: (state) =>
        state['pension-calculator'].outputs
          .worst_retirement_investment_property,
      investmentPropertyBestRetirementAge: (state) =>
        state['pension-calculator'].outputs.best_retirement_investment_property,
      isInvestmentPropertyOneTimeInvestmentSufficient: (state) =>
        state['pension-calculator'].outputs
          .boolean_investment_property_one_time_sufficient,
      isInvestmentPropertyMonthlyInvestmentSufficient: (state) =>
        state['pension-calculator'].outputs
          .boolean_investment_property_monthly_contribution_sufficient,
      isOwnHomeOneTimeInvestmentSufficient: (state) =>
        state['pension-calculator'].outputs.boolean_home_one_time_sufficient,
      isOwnHomeMonthlyInvestmentSufficient: (state) =>
        state['pension-calculator'].outputs
          .boolean_home_monthly_contribution_sufficient,
    }),

    playgroundSolutionCards() {
      return this.playgroundSolutionCardsData.map((card) => {
        card.title = this.$t(`ctaTitles.${card.name}`);
        card.heading = this.$t(`headings.${card.name}`);
        card.image = this.getCardImage(card.name);
        card.tooltip = this.$t(`tooltips.${card.name}`);

        return card;
      });
    },
  },

  methods: {
    /**
     * @param {PENSION_SOLUTION} cardName
     * @returns {boolean}
     */
    isContributionTooLow(cardName) {
      return (
        this.isOneTimeInvestmentTooLow(cardName) ||
        this.isMonthlyInvestmentTooLow(cardName)
      );
    },

    /**
     * @param {PENSION_SOLUTION} cardName
     * @returns {boolean}
     */
    isOneTimeInvestmentTooLow(cardName) {
      if (cardName === 'ownHome') {
        return !this.isOwnHomeOneTimeInvestmentSufficient;
      }

      if (cardName === 'investmentProperty') {
        return !this.isInvestmentPropertyOneTimeInvestmentSufficient;
      }

      return false;
    },

    /**
     * @param {PENSION_SOLUTION} cardName
     * @returns {boolean}
     */
    isMonthlyInvestmentTooLow(cardName) {
      if (cardName === 'ownHome') {
        return !this.isOwnHomeMonthlyInvestmentSufficient;
      }

      if (cardName === 'investmentProperty') {
        return !this.isInvestmentPropertyMonthlyInvestmentSufficient;
      }

      return false;
    },

    /**
     * @typedef CtaButtonLink
     * @property {'criteriaStart' | 'pensionFlow' | 'propSearchLogin' | 'affordability' | 'properties' | ''} name
     * @property {string} href - link value
     */

    /**
     * @param {PENSION_SOLUTION} cardName
     * @returns {CtaButtonLink}
     */
    getCtaButtonLink(cardName) {
      if (cardName === PENSION_SOLUTION.ownHome) {
        return {
          name: this.isLoggedIn ? 'affordability' : 'criteriaStart',
          href: this.$t(PENSION_CALCULATOR_CTA_LINKS['hypofriend-appointment']),
        };
      }

      if (cardName === PENSION_SOLUTION.investmentProperty) {
        if (this.retirementCalculatorAbExperiment) {
          return {
            name: 'investfriend',
            href: this.$t(
              PENSION_CALCULATOR_CTA_LINKS['investfriend-appointment'],
            ),
          };
        } else {
          return {
            name: this.isLoggedIn ? 'properties' : 'propSearchLogin',
            href: this.$t(
              PENSION_CALCULATOR_CTA_LINKS['investfriend-appointment'],
            ),
          };
        }
      }

      if (cardName === PENSION_SOLUTION.privatePensionPlan) {
        if (this.retirementCalculatorAbExperiment) {
          return {
            name: 'pensionfriend',
            href: this.$t(
              PENSION_CALCULATOR_CTA_LINKS['pensionfriend-appointment'],
            ),
          };
        } else {
          return {
            name: 'pensionfriend',
            href: this.$t(
              PENSION_CALCULATOR_CTA_LINKS['pensionfriend-appointment'],
            ),
          };
        }
      }
    },

    isHeyflowURL(cardName) {
      return cardName === PENSION_SOLUTION.privatePensionPlan;
    },

    getItemTooltip(cardName, name) {
      if (name === this.$t('items.monthlyPension')) {
        return this.$t('monthlyPensionTooltip');
      }

      if (name === this.$t('items.monthlyPayouts')) {
        return this.$t('monthlyPayoutTooltip');
      }

      if (name === this.$t('items.retirementAge')) {
        let best, expected, worst;

        if (cardName === 'ownHome') {
          expected = this.ownHomeExpectedRetirementAge;
        } else if (cardName === 'privatePensionPlan') {
          best = this.privatePensionPlanBestRetirementAge;
          expected = this.privatePensionPlanExpectedRetirementAge;
          worst = this.privatePensionPlanWorstRetirementAge;
        } else if (cardName === 'investmentProperty') {
          best = this.investmentPropertyBestRetirementAge;
          expected = this.investmentPropertyExpectedRetirementAge;
          worst = this.investmentPropertyWorstRetirementAge;
        }

        return this.$t(`retirementAgeTooltips.${cardName}`, {
          best,
          expected,
          worst,
        });
      }
    },

    getItemErrorTooltip(cardName, name) {
      let tooltip = null;
      const monthlyInvestmentType = 'monthlyInvestment';
      const oneTimeInvestmentType = 'oneTimeInvestment';

      const itemType =
        name === this.$t('items.monthlyInvestment')
          ? monthlyInvestmentType
          : name === this.$t('items.oneTimeInvestment')
          ? oneTimeInvestmentType
          : null;

      if (
        itemType === monthlyInvestmentType &&
        this.isMonthlyInvestmentTooLow(cardName)
      ) {
        tooltip = this.$t('tooLowMonthlyPayoutTooltip');
      }

      if (
        itemType === oneTimeInvestmentType &&
        this.isOneTimeInvestmentTooLow(cardName)
      ) {
        tooltip =
          cardName === 'ownHome'
            ? this.$t('tooLowOneTimeInvestmentOwnHomeTooltip')
            : cardName === 'investmentProperty'
            ? this.$t('tooLowOneTimeInvestmentInvestmentPropertyTooltip')
            : null;
      }

      return tooltip;
    },

    async trackCtaButtonClick(name) {
      const labels = {
        ownHome: 'See what you can afford',
        privatePensionPlan: 'Request your pension offer',
        investmentProperty: 'See property options',
      };

      if (!labels[name]) {
        return;
      }

      await this.$eventTrackClick(
        'Clicked Website Pension Calculator CTA Button',
        {
          Label: labels[name],
        },
      );
    },

    proceedItems(items) {
      return Object.keys(items)
        .map((key) => {
          const cardValues = {
            name: this.$t(`items.${key}`),
            value: items[key],
          };
          if (key === 'effectiveCosts') {
            return {
              ...cardValues,
              tooltip: this.$t(`${key}-tooltip`),
            };
          }
          return cardValues;
        })
        .filter((item) => item.value !== null);
    },

    /**
     * @param {PENSION_SOLUTION} cardName
     * @returns {any} - svg image
     */
    getCardImage(cardName) {
      const images = {
        ownHome: require('@/assets/svg/big-house.svg'),
        investmentProperty: require('@/assets/svg/apartment.svg'),
        privatePensionPlan: require('@/assets/svg/growing-money.svg'),
      };

      return images[cardName];
    },
  },
};
